import React, { useEffect, useId, useRef, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import './InlineFullClip.css';
import { trackCommunity } from './analytics';

export default function InlineFullClip({ url, onRefresh, onPlay }) {
  const panelId = useId();
  const video = useRef(null);
  const retryRevision = useRef(0);
  const [openedUrl, setOpenedUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [generation, setGeneration] = useState(0);
  // Comparing the URL during render prevents a newly assigned recording from
  // being mounted before an effect has time to close the previous recording.
  const isOpen = Boolean(url) && openedUrl === url;
  // Full recordings start at normal speed independently of the review track.
  const speed = 1;

  useEffect(() => {
    setOpenedUrl(null); setFailed(false); setRetrying(false); setGeneration(0);
    return () => { retryRevision.current += 1; };
  }, [url]);
  useEffect(() => {
    if (video.current) {
      video.current.defaultPlaybackRate = speed;
      video.current.playbackRate = speed;
    }
  }, [generation, isOpen, speed, url]);

  const toggle = () => {
    trackCommunity(isOpen ? 'motion_full_clip_hidden' : 'motion_full_clip_opened');
    retryRevision.current += 1;
    setFailed(false); setRetrying(false);
    setOpenedUrl(isOpen ? null : url);
  };
  const retry = async () => {
    const revision = ++retryRevision.current;
    setRetrying(true);
    try {
      if (onRefresh) await onRefresh();
      if (revision !== retryRevision.current) return;
      setFailed(false); setGeneration((value) => value + 1); setRetrying(false);
    } catch (_) {
      if (revision === retryRevision.current) { setFailed(true); setRetrying(false); }
    }
  };

  if (!url) return null;
  return <section className="cr-inline-full-clip">
    <button type="button" className="cr-text-button cr-inline-full-clip-toggle" aria-expanded={isOpen} aria-controls={panelId} onClick={toggle}>
      {isOpen ? <FiChevronUp aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}{isOpen ? 'Hide full clip' : 'Show full clip'}
    </button>
    <div id={panelId} role="region" aria-label="Full clip playback" hidden={!isOpen}>
      {isOpen && <>
        <video key={generation} ref={video} className="cr-inline-full-clip-video" src={url} aria-label="Full clip" controls autoPlay muted playsInline disablePictureInPicture controlsList="nodownload noremoteplayback" preload="auto"
          onLoadedMetadata={() => { if (video.current) { video.current.defaultPlaybackRate = speed; video.current.playbackRate = speed; } }}
          onLoadedData={() => setFailed(false)} onError={() => setFailed(true)} onPlay={onPlay} />
        {failed && <p className="cr-error cr-inline-full-clip-error" role="alert">The full clip couldn’t load. <button type="button" className="cr-text-button" onClick={retry} disabled={retrying}>{retrying ? 'Retrying…' : 'Retry full clip'}</button></p>}
      </>}
    </div>
  </section>;
}
